import React, {
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import Picture from '../picture'

enum direction {
  'left' = -1,
  'right' = 1,
}

interface slideStyleProp {
  left: number
  scale: number
  zIndex: number
  idx: number
}

const threshold = 20

interface SlideImage {
  id: number
  webImage: string
  altText: string
}

interface Prop {
  sliderImageList: SlideImage[]
}

const ThreeDSwiper = ({ sliderImageList }: Prop) => {
  const [slideStyles, setSlideStyles] = useState<slideStyleProp[]>([])
  const swiperStartRef = useRef<null | number>(null)

  const checkDirection = (delta: number) => {
    if (delta < -threshold || delta > threshold) {
      moveSwiper(delta > threshold ? direction.right : direction.left)
    }
    swiperStartRef.current = null
  }

  const setAsMidSlide = (index: number) => {
    const noOfSlidesInBetween = Math.floor(slideStyles.length / 2) - index
    setSlideStyles((prev) => {
      const list = [...prev]
      if (noOfSlidesInBetween < 0) {
        list.splice(
          0,
          0,
          ...list.splice(
            list.length + noOfSlidesInBetween,
            -noOfSlidesInBetween
          )
        )
      } else {
        list.splice(list.length - 1, 0, ...list.splice(0, noOfSlidesInBetween))
      }
      return list
    })
  }

  const moveSwiper = (val: direction) => {
    setSlideStyles((prev) => {
      const list = [...prev]
      if (val == direction.right) {
        list.splice(list.length - 1, 0, ...list.splice(0, 1))
      } else {
        list.splice(0, 0, ...list.splice(list.length - 1, 1))
      }
      return list
    })
  }

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    swiperStartRef.current = event.changedTouches[0].screenX
  }

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (swiperStartRef.current != null) {
      const delta = event.changedTouches[0].screenX - swiperStartRef.current
      checkDirection(delta)
    }
  }

  const onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    swiperStartRef.current = event.screenX
    event.preventDefault()
  }

  const onMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    if (swiperStartRef.current != null) {
      const delta = event.screenX - swiperStartRef.current
      checkDirection(delta)
    }
  }

  const onContainerMouseOut = (event: MouseEvent<HTMLDivElement>) => {
    if (swiperStartRef.current != null) {
      const delta = event.screenX - swiperStartRef.current
      checkDirection(delta)
    }
  }

  const getInitialState = useCallback((): slideStyleProp[] => {
    if (sliderImageList.length == 0) return []
    const isEven = sliderImageList.length % 2 === 0
    const len = sliderImageList.length
    const slideStyleList = new Array(len)
    const arr = new Array(Math.floor((len - 1) / 2)).fill(1)
    const zIndexs = [10, 100]
    const zDiff = (zIndexs[1] - zIndexs[0]) / arr.length
    const scales = [0.6, 1]
    const scaleDiff = (scales[1] - scales[0]) / arr.length
    const lefts = [-20, 0]
    const leftDiff = (lefts[1] - lefts[0]) / arr.length
    const initialObj = {
      zIndex: zIndexs[0] - zDiff,
      scale: scales[0] - scaleDiff,
      left: lefts[0] - leftDiff,
    }
    arr.reduce((acc, ele, idx) => {
      const val = {
        zIndex: acc.zIndex + zDiff,
        scale: acc.scale + scaleDiff,
        left: acc.left + leftDiff,
      }
      isEven ? (slideStyleList[idx + 1] = val) : (slideStyleList[idx] = val)
      slideStyleList[len - idx - 1] = val
      return val
    }, initialObj)
    const midVal = {
      zIndex: zIndexs[1],
      scale: scales[1],
      left: lefts[1],
    }
    if (isEven) {
      slideStyleList[0] = initialObj
      slideStyleList[arr.length + 1] = midVal
    } else {
      slideStyleList[arr.length] = midVal
    }
    for (
      let i = Math.floor(slideStyleList.length / 2);
      i < slideStyleList.length;
      i++
    ) {
      slideStyleList[i] = {
        ...slideStyleList[i],
        left: -slideStyleList[i].left,
      }
    }
    return slideStyleList.map((ele, idx) => ({ ...ele, idx }))
  }, [sliderImageList.length])

  useEffect(() => {
    setSlideStyles(getInitialState())
  }, [getInitialState])

  return (
    <section className="w-full h-[600px] block mt-24">
      <div
        className="relative w-full left-1/2 -translate-x-36 h-full flex items-center justify-center"
        onMouseOut={onContainerMouseOut}
        title="OuterContainer"
      >
        {sliderImageList.length && slideStyles.length ? (
          <>
            {sliderImageList.map((slide, index) => (
              <div
                id={'Slide-' + slide.id}
                title={slide.altText}
                key={'Slide-' + slide.id}
                onClick={(e) => {
                  setAsMidSlide(slideStyles[index].idx)
                }}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
                className="absolute inline-flex h-[500px] w-[280px] shadow-2xl transition-all ease-in-out duration-1000"
                style={{
                  left: `${slideStyles[index]?.left}%`,
                  transform: `scale(${slideStyles[index]?.scale})`,
                  zIndex: Math.floor(slideStyles[index]?.zIndex),
                }}
              >
                <Picture image={slide.webImage} imageAlt={slide.altText} />
              </div>
            ))}
          </>
        ) : null}
      </div>
    </section>
  )
}

export default React.memo(ThreeDSwiper)
